import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { lastValueFrom, take } from 'rxjs';
import { AppResource } from 'src/app/app.resource';
import { PathType } from 'src/app/core/enums/path-type.enum';
import {
  NatureResidence,
  QualiteOccupant,
  SuperficieChambre
} from 'src/app/core/models/form-state.model';
import { Question } from 'src/app/core/models/question.model';
import { cloneResumeStep } from 'src/app/core/models/resume-step.model';
import { Step } from 'src/app/core/models/step.model';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import * as fromHousing from '../../../core/state/housing';
import * as fromInfo from '../../../core/state/info';
import * as fromPath from '../../../core/state/path';

import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Stepper } from 'src/app/core/models/stepper.model';
import { State } from 'src/app/core/state/core.state';
import { MatomoConstants } from 'src/app/shared/constants/matomo.constants';
import { INSURED_VALUE_PATH, ROOT_PATH } from 'src/app/shared/constants/route.constants';

export interface RoomArrayData {
  label: string;
  value: SuperficieChambre;
  class: string;
  selected: boolean;
}
@Component({
  selector: 'app-room-area',
  templateUrl: './room-area.component.html',
  styleUrls: ['./room-area.component.scss']
})
export class RoomAreaComponent extends BaseComponent implements OnInit {
  nextStepEnabled = false;

  pathType = PathType;
  path: string = '';

  override step: Step = {
    step: this.resource.header.stepHousing,
    stepNumber: 1,
    subStep: this.resource.header.subStepHouseApartment,
    subStepNumber: 2
  };

  override question: Question = {
    libelle: this.resource.question.roomArea,
    popupInfo: undefined
  };

  public override currentPage = MatomoConstants.TYPE_HABITATION;
  contentWebCallBackPopup: string = '';
  displayModal: boolean = false;

  formRoomArea: FormArray<
    FormGroup<{
      label: FormControl<string>;
      value: FormControl<SuperficieChambre>;
      class: FormControl<string>;
      selected: FormControl<boolean>;
    }>
  > = new FormArray<
    FormGroup<{
      label: FormControl<string>;
      value: FormControl<SuperficieChambre>;
      class: FormControl<string>;
      selected: FormControl<boolean>;
    }>
  >([]);

  constructor(store: Store<State>, resources: AppResource, private readonly router: Router) {
    super(store, resources);
  }

  override async ngOnInit() {
    super.ngOnInit();
    this.initForm();
    this.updateResumeStepVisibility(true);
    this.loadStateData();
  }

  private initForm() {
    const arrayRoomArea: RoomArrayData[] = [
      {
        label: this.resource.roomArea.CROUSLessThan10,
        value: SuperficieChambre.CROUSMoinsDe10m2,
        class: 'appartment',
        selected: false
      },
      {
        label: this.resource.roomArea.LessThan10,
        value: SuperficieChambre.MoinsDe10m2,
        class: 'less-10',
        selected: false
      },
      {
        label: this.resource.roomArea.Between11And40,
        value: SuperficieChambre.De11a40m2,
        class: 'between-11-40',
        selected: false
      },
      {
        label: this.resource.roomArea.MoreThan40,
        value: SuperficieChambre.PlusDe40m2,
        class: 'more-40',
        selected: false
      }
    ];

    arrayRoomArea.forEach((item: RoomArrayData) => {
      this.formRoomArea.push(
        new FormGroup<{
          label: FormControl<string>;
          value: FormControl<SuperficieChambre>;
          class: FormControl<string>;
          selected: FormControl<boolean>;
        }>({
          label: new FormControl<string>(item.label) as FormControl,
          value: new FormControl<SuperficieChambre>(item.value) as FormControl,
          class: new FormControl<string>(item.class) as FormControl,
          selected: new FormControl<boolean>(item.selected) as FormControl
        })
      );
    });
  }

  private async loadStateData() {
    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));
    let roomAreaFromStore = await lastValueFrom(
      this.store.select(fromHousing.selectRoomArea).pipe(take(1))
    );
    if (roomAreaFromStore) {
      this.formRoomArea.controls
        .find(i => i.value.value === roomAreaFromStore)!
        .patchValue({ selected: true });
    }
    if (this.path === PathType.HOUSING_ETUDIANTE) {
      this.contentWebCallBackPopup = this.resource.webcallback.otherHE;
    }
  }

  onRoomAreaSelected(value: SuperficieChambre) {
    // select one and unselect the others
    let selected = this.formRoomArea.controls.find(i => i.controls.value.value === value);
    selected!.patchValue({ selected: true });

    this.formRoomArea.controls.forEach(i => {
      if (i.controls.value.value !== value) {
        i.patchValue({ selected: false });
      }
    });

    this.store.dispatch(
      fromHousing.patchHousingType({
        payload: { SuperficieChambre: value }
      })
    );

    if (value === SuperficieChambre.PlusDe40m2) {
      this.displayModal = true;
    } else {
      this.nextStep();
    }
  }

  async nextStep() {
    await this.updateResumeStep();

    await this.updateStepper(this.path);

    this.router.navigate([`${ROOT_PATH}${INSURED_VALUE_PATH}`]);
  }

  cancel() {
    this.displayModal = false;
  }

  async updateStepper(path: string) {
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));
    let tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    // get quality occupant from store
    // and set it to "locataire" if it's not already set
    this.store.dispatch(
      fromHousing.patchOccupantType({
        payload: { QualiteOccupant: QualiteOccupant.Locataire }
      })
    );
    // set ResidentType to prinicipal
    this.store.dispatch(
      fromHousing.patchHousingNature({
        payload: { NatureResidence: NatureResidence.Principale }
      })
    );

    let title = tmpStepper.devis.titles.find(
      (x: { libelle: string }) => x.libelle == this.resource.stepper.lodging
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.houseapartment
    );

    if (subtitleS) subtitleS.isValid = true;

    let subtitleN = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.ownertenant
    );

    if (subtitleN) {
      subtitleN.isActive = true;
      subtitleN.isVisible = true;
    }

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }

  async updateResumeStep() {
    const resumeStep = await lastValueFrom(
      this.store.select(fromInfo.selectCurrentResumeStep).pipe(take(1))
    );
    const tmpResumeStep = cloneResumeStep(resumeStep);

    const stepResumeStep = tmpResumeStep.steps?.find(
      x => x.id == this.resource.resumestep.roomarea
    );
    let libelle = '';

    libelle = String(
      this.formRoomArea.controls.find(i => i.value.selected === true)!.controls.value.value
    );

    if (stepResumeStep && libelle != '') {
      stepResumeStep.step = libelle;
      stepResumeStep.isVisible = true;
    } else {
      tmpResumeStep.steps?.push({
        id: this.resource.resumestep.roomarea,
        step: libelle,
        url: this.resource.resumestep.roomarea,
        isVisible: true
      });
    }
    this.store.dispatch(fromInfo.updateResumeStep({ payload: tmpResumeStep }));
  }
}
